import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { next } from '@ember/runloop';
import { modifier } from 'ember-modifier';
import style from 'ember-style-modifier/modifiers/style';
import {
  setupCanvas,
  roundedRectGradient,
  drawGridLines,
  drawYAxisLabels,
  drawXAxisLabels,
} from '../../utils/charts/canvas-utils.ts';
import { linearScale, bandScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, formatNumber } from '../../utils/charts/chart-layout.ts';
import {
  DEFAULT_CHART_COLORS,
  getThemeColors,
  hexToRgba,
} from '../../utils/charts/color-palette.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import clampTooltip from '../../modifiers/clamp-tooltip.ts';
import type { ChartDataPoint, ChartOptions, AxisOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// BarChart – Canvas-rendered vertical bar chart
// ---------------------------------------------------------------------------

interface BarChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Fixed height in pixels (default: 300) */
      height?: number;
    };
  Element: HTMLDivElement;
}

const DEFAULT_HEIGHT = 300;

export default class BarChart extends Component<BarChartSignature> {
  @tracked containerWidth = 400;
  @tracked containerHeight = 0;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  get height() {
    if (this.args.height) return this.args.height;
    return this.containerHeight > 0 ? this.containerHeight : DEFAULT_HEIGHT;
  }

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get data() {
    return this.args.data ?? [];
  }

  get shouldRotateLabels() {
    if (!this.data.length) return false;
    // Rotate when bars are narrow or there are many categories
    const avgBarWidth = this.containerWidth / this.data.length;
    return this.data.length > 5 || avgBarWidth < 60;
  }

  get tooltipData() {
    if (this.hoveredIndex === null || !this.data[this.hoveredIndex]) return null;
    const d = this.data[this.hoveredIndex]!;
    return { category: d.category, value: formatNumber(d.value) };
  }

  installCanvas = modifier((element: HTMLElement) => {
    const canvas = element.querySelector('canvas');
    if (!canvas) return;

    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      if (entry.contentRect.height > 0) {
        this.containerHeight = entry.contentRect.height;
      }
      this.draw(canvas);
    });
    observer.observe(element);
    // eslint-disable-next-line ember/no-runloop -- one-shot post-insert measure scheduled for the next tick; tied to the element's lifetime
    next(this, () => {
      this.containerWidth = element.clientWidth || 400;
      this.containerHeight = element.clientHeight || 0;
      this.draw(canvas);
    });

    // Mouse interaction
    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => {
      this.hoveredIndex = null;
      this.draw(canvas);
    };
    const onClick = (e: MouseEvent) => this.handleClick(e, canvas);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  });

  // Store bar rects for hit testing
  private barRects: Array<{ x: number; y: number; w: number; h: number; index: number }> = [];

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const data = this.data;
    if (!data.length) return;

    const bottomMargin = this.shouldRotateLabels ? 160 : 40;
    const layout = computeLayout(width, height, { bottom: bottomMargin, left: 55 });
    const theme = getThemeColors();

    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    // Scales
    const maxVal = Math.max(...data.map((d) => d.value), 0);
    const ticks = niceAxisTicks(0, maxVal);
    const yScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.top + layout.innerHeight, layout.top]
    );
    const categories = data.map((d) => d.category);
    const xScale = bandScale(categories, [layout.left, layout.left + layout.innerWidth], 0.2);

    // Grid lines
    drawGridLines(ctx, ticks, yScale, layout.left, layout.left + layout.innerWidth, theme.grid);

    // Y axis labels
    const formatFn = this.args.formatValue ?? formatAxisValue;
    drawYAxisLabels(ctx, ticks, yScale, layout.left - 8, theme.textMuted, formatFn);

    // X axis labels (with thinning for dense data)
    const positions = categories.map((c) => xScale(c) + xScale.bandwidth() / 2);
    const maxLabels = Math.max(
      4,
      Math.floor(layout.innerWidth / (this.shouldRotateLabels ? 40 : 70))
    );
    drawXAxisLabels(
      ctx,
      categories,
      positions,
      layout.top + layout.innerHeight + 8,
      theme.textMuted,
      this.shouldRotateLabels,
      maxLabels
    );

    // Bars — gradient fill with glow on hover
    this.barRects = [];
    const bw = xScale.bandwidth();
    data.forEach((d, i) => {
      const x = xScale(d.category);
      const barH = layout.top + layout.innerHeight - yScale(d.value);
      const y = yScale(d.value);
      const color = this.colors[i % this.colors.length]!;
      const lighter = adjustBrightness(color, 1.4);
      const isHovered = this.hoveredIndex === i;

      if (isHovered) {
        // Hovered: brighter with glow
        roundedRectGradient(
          ctx,
          x,
          y,
          bw,
          barH,
          adjustBrightness(color, 1.15),
          hexToRgba(lighter, 0.5),
          4,
          true
        );
      } else {
        // Normal: subtle gradient
        roundedRectGradient(ctx, x, y, bw, barH, color, hexToRgba(lighter, 0.37), 4, false);
      }

      this.barRects.push({ x, y, w: bw, h: barH, index: i });
    });

    // Axis labels
    if (this.args.xLabel) {
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(this.args.xLabel, layout.left + layout.innerWidth / 2, height - 4);
    }
    if (this.args.yLabel) {
      ctx.save();
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.translate(14, layout.top + layout.innerHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(this.args.yLabel, 0, 0);
      ctx.restore();
    }
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit) {
      this.hoveredIndex = hit.index;
      this.tooltipX = rect.left + hit.x + hit.w / 2;
      this.tooltipY = rect.top + hit.y - 8;
      this.draw(canvas);
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit && this.args.onClick && this.data[hit.index]) {
      this.args.onClick(this.data[hit.index]!);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="bar"
      class="relative w-full h-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas
        role="img"
        aria-label="Bar chart"
        {{style width=(concat this.containerWidth "px") height=(concat this.height "px")}}
      ></canvas>

      {{! Tooltip overlay }}
      {{#if this.tooltipData}}
        <div
          data-test-chart-tooltip
          class="tio-chart-tooltip -translate-x-1/2 -translate-y-full"
          {{style left=(concat this.tooltipX "px") top=(concat this.tooltipY "px")}}
          {{clampTooltip (concat this.tooltipX ":" this.tooltipY)}}
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="tio-text-muted">{{this.tooltipData.value}}</div>
        </div>
      {{/if}}
    </div>
  </template>
}

function adjustBrightness(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v * factor)));
  const toHex = (v: number) => clamp(v).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export type { BarChartSignature };
